import { useEffect, useRef } from "react";
import { mediaBoxStyle, videoPlayback, type VideoPart } from "@/lib/activityAnatomy";
import { post } from "@/lib/api";
import {
  SEEK_SLACK_SEC,
  mergeWatchEntry,
  normalizeWatchEntry,
  type WatchEntry,
} from "@/lib/mediaWatch";

type Props = {
  part: VideoPart;
  watchKey: string;
  activityId: string;
  locked: boolean;
  initial: WatchEntry | undefined;
  onWatch: (key: string, entry: WatchEntry) => void;
};

/**
 * Uploaded file video. When locked, the playhead cannot jump past the furthest
 * time they have actually played. YouTube/Vimeo never use this component.
 */
export default function StudentLockedVideo({
  part,
  watchKey,
  activityId,
  locked,
  initial,
  onWatch,
}: Props) {
  const play = videoPlayback(part.url);
  const box = mediaBoxStyle(part, { defaultRatio: "16 / 9" });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const allowedRef = useRef(normalizeWatchEntry(initial).maxSeconds);
  const durationRef = useRef(normalizeWatchEntry(initial).durationSeconds);
  const endedRef = useRef(normalizeWatchEntry(initial).ended);
  const seekingRef = useRef(false);
  const lastSentRef = useRef(0);

  useEffect(() => {
    const next = normalizeWatchEntry(initial);
    allowedRef.current = Math.max(allowedRef.current, next.maxSeconds);
    durationRef.current = Math.max(durationRef.current, next.durationSeconds);
    endedRef.current = endedRef.current || next.ended;
  }, [initial]);

  if (!play || play.kind !== "file") return null;

  const clampSeek = (video: HTMLVideoElement) => {
    if (!locked) return;
    if (video.currentTime > allowedRef.current + SEEK_SLACK_SEC) {
      video.currentTime = allowedRef.current;
    }
  };

  const emit = (partial: Partial<WatchEntry>, force = false) => {
    const duration = durationRef.current;
    if (duration <= 0 && !force) return;
    const next = mergeWatchEntry(
      {
        maxSeconds: allowedRef.current,
        durationSeconds: duration,
        ended: endedRef.current,
      },
      {
        maxSeconds: partial.maxSeconds ?? allowedRef.current,
        durationSeconds: partial.durationSeconds ?? duration,
        ended: partial.ended ?? endedRef.current,
      },
    );
    allowedRef.current = next.maxSeconds;
    durationRef.current = next.durationSeconds;
    endedRef.current = next.ended;
    onWatch(watchKey, next);
    const now = Date.now();
    const send = () =>
      post<{ success: boolean; data?: { mediaWatch?: unknown } }>(
        `/api/student/activities/${encodeURIComponent(activityId)}/watch`,
        {
          partKey: watchKey,
          maxSeconds: next.maxSeconds,
          durationSeconds: next.durationSeconds,
          ended: next.ended,
        },
      ).catch(() => {});
    if (force) {
      lastSentRef.current = now;
      return send();
    }
    if (now - lastSentRef.current < 4000) return;
    lastSentRef.current = now;
    void send();
  };

  return (
    <div className="student-body-video-wrap">
      <video
        ref={videoRef}
        className="student-body-video"
        src={play.src}
        controls
        playsInline
        preload="metadata"
        controlsList="nodownload"
        disablePictureInPicture
        style={box}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          durationRef.current = Math.max(durationRef.current, video.duration || 0);
          emit({ durationSeconds: durationRef.current }, true);
        }}
        onTimeUpdate={(e) => {
          const video = e.currentTarget;
          if (seekingRef.current) return;
          if (locked && video.currentTime > allowedRef.current + SEEK_SLACK_SEC) {
            video.currentTime = allowedRef.current;
            return;
          }
          if (!video.paused) {
            allowedRef.current = Math.max(allowedRef.current, video.currentTime);
            durationRef.current = Math.max(durationRef.current, video.duration || 0);
            emit({});
          }
        }}
        onSeeking={(e) => {
          seekingRef.current = true;
          clampSeek(e.currentTarget);
        }}
        onSeeked={(e) => {
          clampSeek(e.currentTarget);
          seekingRef.current = false;
        }}
        onPause={(e) => {
          const video = e.currentTarget;
          allowedRef.current = Math.max(allowedRef.current, video.currentTime);
          emit({}, true);
        }}
        onEnded={(e) => {
          const video = e.currentTarget;
          durationRef.current = Math.max(durationRef.current, video.duration || 0);
          allowedRef.current = Math.max(allowedRef.current, durationRef.current);
          endedRef.current = true;
          emit({ ended: true, maxSeconds: durationRef.current }, true);
        }}
      >
        Your browser cannot play this video.
      </video>
      {locked ? (
        <p className="student-body-video-lock">Skipping ahead is off until you watch to the end.</p>
      ) : null}
    </div>
  );
}
